import {
  OnboardingConversation as Conversation,
  OnboardingAction,
} from '@chiroup/core/types/Onboarding.type';
import { ChevronDown, MessageSquare } from 'lucide-react';
import { FormEvent, useContext, useState } from 'react';
import {
  buttonClass,
  errorMessage,
  inputClass,
  instantLabel,
  OnboardingDraftContext,
  secondaryClass,
  Status,
} from './OnboardingUi';

type Props = {
  conversation?: Conversation;
  target: 'task' | 'file';
  targetId: string;
  editable: boolean;
  busy: boolean;
  act: (action: OnboardingAction) => Promise<unknown>;
};

const OnboardingConversation = ({ conversation, target, targetId, editable, busy, act }: Props) => {
  const draft = useContext(OnboardingDraftContext);
  const [localText, setLocalText] = useState('');
  const [sending, setSending] = useState(false);
  const draftKey = `${target}:${targetId}`;
  const text = draft ? draft.drafts[draftKey] || '' : localText;
  const setText = (value: string) => (draft ? draft.change(draftKey, value) : setLocalText(value));
  const [error, setError] = useState('');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const submitted = text;
    if (!submitted.trim() || sending || busy) return;
    setSending(true);
    setError('');
    try {
      await act({ type: 'conversation', target, targetId, text: submitted.trim() });
      if (draft) draft.clearSubmitted(draftKey, submitted);
      else setLocalText(current => (current === submitted ? '' : current));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSending(false);
    }
  };
  const changeStatus = async () => {
    setError('');
    try {
      await act({
        type: 'conversation',
        target,
        targetId,
        status: conversation?.status === 'open' ? 'resolved' : 'open',
      });
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  };
  return (
    <details className="group/conversation overflow-hidden rounded-xl border border-border bg-background">
      <summary className="flex cursor-pointer list-none flex-wrap items-center gap-2 px-4 py-3 text-base font-medium hover:bg-muted/40 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-700 dark:focus-visible:outline-primary-400 sm:text-sm">
        <MessageSquare
          aria-hidden="true"
          className="h-4 w-4 text-primary-700 dark:text-primary-300"
        />
        <p className="flex-1">Conversation · {conversation?.messages.length || 0}</p>
        {!!conversation?.messages.length && <Status value={conversation.status} />}
        <ChevronDown
          aria-hidden="true"
          className="h-4 w-4 text-muted-foreground motion-safe:transition-transform group-open/conversation:rotate-180"
        />
      </summary>
      <div className="space-y-4 border-t border-border p-4">
        {conversation?.messages.length ? (
          <ol role="list" className="space-y-4">
            {conversation.messages.map(message => (
              <li
                key={message.id}
                className={`space-y-2 rounded-xl p-4 text-base sm:text-sm ${message.author.internal ? 'bg-sky-50/65 dark:bg-sky-950/25' : 'bg-muted/45'}`}
              >
                <p className="font-semibold">
                  {message.author.name} {message.author.internal && '· ChiroUp'}
                </p>
                <p className="text-muted-foreground">
                  <time dateTime={message.createdAt}>{instantLabel(message.createdAt)}</time>
                </p>
                <p className="whitespace-pre-wrap break-words">{message.text}</p>
              </li>
            ))}
          </ol>
        ) : (
          <div className="flex items-start gap-3 rounded-xl bg-muted/35 p-4">
            <MessageSquare className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p className="text-base text-muted-foreground sm:text-sm">
              No messages yet. Keep questions, decisions, and follow-up together here.
            </p>
          </div>
        )}
        {editable && (
          <form onSubmit={submit} className="space-y-3">
            <textarea
              name={`conversation-${targetId}`}
              aria-label="Add a message"
              className={inputClass}
              value={text}
              onChange={event => setText(event.target.value)}
              maxLength={5000}
              rows={3}
              placeholder="Add a note or question…"
              required
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="submit"
                className={buttonClass}
                disabled={busy || sending || !text.trim()}
              >
                {sending ? 'Sending…' : 'Send message'}
              </button>
              {!!conversation?.messages.length && (
                <button
                  type="button"
                  className={secondaryClass}
                  disabled={busy || sending}
                  onClick={changeStatus}
                >
                  {conversation.status === 'open' ? 'Resolve conversation' : 'Reopen conversation'}
                </button>
              )}
            </div>
          </form>
        )}
        {error && (
          <p role="alert" className="text-base text-red-600 dark:text-red-400 sm:text-sm">
            {error}
          </p>
        )}
      </div>
    </details>
  );
};

export default OnboardingConversation;
